import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, AtSign, UserPlus, MessageSquare, X, Settings, Sun } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const notificationIcons = {
  mention: AtSign,
  assignment: UserPlus,
  reply: MessageSquare
};

const notificationColors = {
  mention: 'text-pink-500 bg-pink-50',
  assignment: 'text-purple-500 bg-purple-50',
  reply: 'text-green-500 bg-green-50'
};

export default function NotificationCenter({ onNavigateToTask }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    email_disabled: false,
    mentions_disabled: false,
    assignments_disabled: false,
    replies_disabled: false
  });
  
  const token = localStorage.getItem('token');
  const pollInterval = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPreferences(data.preferences || {});
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      await fetch(`${API_URL}/api/task-notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPrefs)
      });
      setPreferences(newPrefs);
    } catch (err) {
      console.error('Error updating preferences:', err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/task-notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/task-notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
    if (onNavigateToTask && notification.task_id) {
      onNavigateToTask(notification.task_id, notification.comment_id);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    
    // Poll for new notifications every 30 seconds
    pollInterval.current = setInterval(fetchUnreadCount, 30000);
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="notification-bell"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={markAllAsRead}
                  className="text-xs"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Tout marquer lu
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => { setIsOpen(false); setShowSettings(true); }}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-96">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Bell className="h-12 w-12 mb-4 opacity-30" />
                <p>Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => {
                  const Icon = notificationIcons[notification.type] || Bell;
                  const colorClass = notificationColors[notification.type] || 'text-gray-500 bg-gray-50';
                  
                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        "w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                        !notification.is_read && "bg-blue-50/50 dark:bg-blue-900/20"
                      )}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={cn("p-2 rounded-full", colorClass)}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={cn(
                              "text-sm",
                              !notification.is_read && "font-medium"
                            )}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-400">
                              {formatDistanceToNow(parseISO(notification.created_at), { 
                                addSuffix: true, 
                                locale: fr 
                              })}
                            </span>
                            {notification.triggered_by_name && (
                              <span className="text-xs text-gray-400">
                                • {notification.triggered_by_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Paramètres des notifications</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Notifications par email</p>
                <p className="text-sm text-gray-500">Recevoir des emails pour les alertes importantes</p>
              </div>
              <Switch
                checked={!preferences.email_disabled}
                onCheckedChange={(checked) => 
                  updatePreferences({ ...preferences, email_disabled: !checked })
                }
              />
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Types de notifications</p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AtSign className="h-4 w-4 text-pink-500" />
                    <span className="text-sm">Mentions (@)</span>
                  </div>
                  <Switch
                    checked={!preferences.mentions_disabled}
                    onCheckedChange={(checked) => 
                      updatePreferences({ ...preferences, mentions_disabled: !checked })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-purple-500" />
                    <span className="text-sm">Assignations de tâches</span>
                  </div>
                  <Switch
                    checked={!preferences.assignments_disabled}
                    onCheckedChange={(checked) => 
                      updatePreferences({ ...preferences, assignments_disabled: !checked })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Réponses aux commentaires</span>
                  </div>
                  <Switch
                    checked={!preferences.replies_disabled}
                    onCheckedChange={(checked) => 
                      updatePreferences({ ...preferences, replies_disabled: !checked })
                    }
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium flex items-center gap-2">
                    <Sun className="h-4 w-4 text-amber-500" />
                    Résumé quotidien
                  </p>
                  <p className="text-sm text-gray-500">Email matinal (7h00, lun-ven) avec vos tâches du jour</p>
                </div>
                <Switch
                  checked={!preferences.digest_disabled}
                  onCheckedChange={(checked) => 
                    updatePreferences({ ...preferences, digest_disabled: !checked })
                  }
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
